import React, { useCallback, useEffect, useRef, useState, FocusEvent, KeyboardEvent } from 'react';

import { isOnlyDigits } from '../Forms/Validation';

import { AvailableKeyDown } from './VerificationField';

interface VerificationInputProps {
  disabled?: boolean;
  handleChange: (value: string) => void;
  handleKeyDown: (key: AvailableKeyDown) => void;
}

export const VerificationInput = React.memo<VerificationInputProps>(function ({
  disabled = false,
  handleChange,
  handleKeyDown,
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState<string>('');

  useEffect(() => {
    const pingingStickAnimation = (): void => {
      if (ref.current === document.activeElement) {
        ref.current?.classList.add('text-input-ping');

        setTimeout(() => {
          ref.current?.classList.remove('text-input-ping');
        }, 1000);
      }
    };

    ref.current?.addEventListener('input', pingingStickAnimation);

    return ref.current?.removeEventListener('input', pingingStickAnimation);
  }, []);

  useEffect(() => {
    if (!ref.current) return;

    if (!ref.current.value) {
      ref.current.value = '';
    }
  }, []);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case AvailableKeyDown.RIGHT:
          handleKeyDown(AvailableKeyDown.RIGHT);
          break;
        case AvailableKeyDown.LEFT:
          handleKeyDown(AvailableKeyDown.LEFT);
          break;
        case AvailableKeyDown.UP:
        case AvailableKeyDown.DOWN:
          e.preventDefault();
          break;
        case AvailableKeyDown.BACKSPACE:
        case AvailableKeyDown.DELETE:
          e.preventDefault();
          // TODO: refactor - find better solution
          if (!ref.current) return;
          ref.current.value = '';
          setValue('');
          handleChange('');
          // On move by left key arrow and press backspace
          // On move by right key arrow and press delete
          handleKeyDown(AvailableKeyDown.DELETE);
          break;

        default:
          break;
      }
    },
    [handleChange, handleKeyDown],
  );

  const onInput = useCallback(
    (event: React.FormEvent<HTMLInputElement>): void => {
      event.currentTarget.value = event.currentTarget.value.slice(0, 1);

      const isValidValue = isOnlyDigits(event.currentTarget.value);

      if (!isValidValue) {
        event.currentTarget.value = value;
        return;
      }

      setValue(event.currentTarget.value);
      handleChange(event.currentTarget.value);
    },
    [value, handleChange],
  );

  const onFocus = (e: FocusEvent<HTMLInputElement>): void => {
    e.currentTarget.select();
  };

  return (
    <input
      ref={ref}
      type="number"
      inputMode="numeric"
      maxLength={1}
      minLength={1}
      min={0}
      max={9}
      pattern="[0-9]"
      placeholder="_"
      disabled={disabled}
      onInput={onInput}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      autoComplete="off"
    />
  );
});
